import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Monitor, Smartphone, Eye, Loader2 } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useLazyGetScreenQuery } from "../../../redux/actions/Customer/authCustomer";

import Carousel from "../../../components/customer/Carousel";
import { ProductsTab } from "../../../components/customer/Tabs/ProductsTab";

type PreviewMode = "desktop" | "mobile";

type PreviewSection = {
  sectionId: number;
  sectionName: string;
  sectionType?: string;
  displayOrder: number;
  isVisible: boolean;
  clientType?: number;
  config?: any;
  data?: any[];
  layoutType?: string | null;
};

const SectionPreviewScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sectionId } = useParams();

  const navigationState = location.state as
    | {
        section?: PreviewSection;
        screenId?: number;
        screenName?: string;
      }
    | undefined;

  const getCustomerScreenName = (name?: string) => {
    switch (name?.toLowerCase()) {
      case "products":
      case "productstab":
        return "productsTab";

      case "pets":
      case "pettab":
        return "petTab";

      case "services":
      case "servicetab":
        return "serviceTab";

      default:
        return "productsTab";
    }
  };

  const screenName = getCustomerScreenName(navigationState?.screenName);

  const [previewMode, setPreviewMode] = useState<PreviewMode>("desktop");

  const [getScreen, { data: screenResponse, isFetching, isError }] =
    useLazyGetScreenQuery();

  // ------------------------------------------------------------
  // LOAD CUSTOMER SCREEN
  // ------------------------------------------------------------

  useEffect(() => {
    getScreen({
      screenName,
      clientType: 99,
    });
  }, [getScreen, screenName]);

  // ------------------------------------------------------------
  // FIND SELECTED SECTION
  // ------------------------------------------------------------

  const section = useMemo<PreviewSection | null>(() => {
    if (screenResponse?.sections) {
      const apiSection = screenResponse.sections.find(
        (item: PreviewSection) => item.sectionId === Number(sectionId),
      );

      if (apiSection) {
        return apiSection;
      }
    }

    return navigationState?.section ?? null;
  }, [screenResponse, sectionId, navigationState?.section]);

  // ------------------------------------------------------------
  // BASIC CUSTOMER HANDLERS
  // ------------------------------------------------------------

  const handlers = {
    onProductClick: (product: any) => {
      console.log("Preview product clicked:", product);
    },

    onViewAllClick: (value?: any) => {
      console.log("Preview view all:", value);
    },

    onBrandClick: (brand: any) => {
      console.log("Preview brand clicked:", brand);
    },

    onViewAllProductClick: (value: any) => {
      console.log("Preview view all product:", value);
    },

    handlePeekCarouselClick: (id: any, data: any) => {
      console.log("Preview peek carousel clicked:", id, data);
    },

    handlePetPromoBannerClick: (value?: any) => {
      console.log("Preview pet promo clicked:", value);
    },

    handleCheckoutClick: () => {
      console.log("Preview checkout");
    },

    onViewAllBestSellingToyClick: (value: any) => {
      console.log("Preview best selling toys:", value);
    },

    onCategoryClick: (category: any) => {
      console.log("Preview category clicked:", category);
    },

    onCategorySlideClick: (category: any) => {
      console.log("Preview category slide clicked:", category);
    },

    onViewAllFlashDealsClick: (value: any) => {
      console.log("Preview flash deals:", value);
    },

    onBannerClick: (banner: any) => {
      console.log("Preview banner clicked:", banner);
    },

    handleShopNow: () => {
      console.log("Preview shop now");
    },
  };

  // ------------------------------------------------------------
  // MAIN CAROUSEL
  // ------------------------------------------------------------

  const renderMainCarousel = () => {
    if (!section?.data?.length) {
      return <EmptyPreview message="No banner data available." />;
    }

    const items = section.data.map((item: any) => ({
      ...item,

      imageUrl:
        previewMode === "mobile"
          ? item.mobileImageUrl || item.imageUrl
          : item.imageUrl,

      mobileImageUrl: item.mobileImageUrl || item.imageUrl,
    }));

    return (
      <div className="w-full overflow-hidden rounded-xl">
        <Carousel
          items={items}
          onItemClick={(item) => console.log("Preview hero clicked:", item)}
        />
      </div>
    );
  };

  // ------------------------------------------------------------
  // NORMAL CUSTOMER SECTION
  // ------------------------------------------------------------

  const renderCustomerSection = () => {
    if (!section) {
      return <EmptyPreview message="Section not found." />;
    }

    return (
      <ProductsTab
        orderedSections={[section]}
        data={{}}
        handlers={handlers}
        saverSaleRef={{
          current: null,
        }}
      />
    );
  };

  // ------------------------------------------------------------
  // SECTION RENDERER
  // ------------------------------------------------------------

  const renderPreview = () => {
    if (!section) {
      return <EmptyPreview message="Section not found in productsTab." />;
    }

    if (section.sectionType === "MainCarousel") {
      return renderMainCarousel();
    }

    return renderCustomerSection();
  };

  // ------------------------------------------------------------
  // LOADING
  // ------------------------------------------------------------

  if (isFetching) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={28} className="animate-spin text-slate-500" />

          <p className="text-sm text-slate-500">Loading customer preview...</p>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------
  // ERROR
  // ------------------------------------------------------------

  if (isError) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm font-medium text-red-600">
            Unable to load customer preview.
          </p>

          <button
            type="button"
            onClick={() =>
              getScreen({
                screenName,
                clientType: 99,
              })
            }
            className="mt-4 px-4 py-2 rounded-lg bg-black text-white text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------
  // PAGE
  // ------------------------------------------------------------

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ------------------------------------------------------
          HEADER
      ------------------------------------------------------- */}

      <div className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* LEFT */}

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 transition"
              >
                <ArrowLeft size={16} />
              </button>

              <div>
                <div className="flex items-center gap-2">
                  <Eye size={15} className="text-slate-500" />

                  <h1 className="text-sm font-semibold text-slate-900">
                    Section Preview
                  </h1>
                </div>

                <p className="text-[11px] text-slate-400 mt-0.5">
                  Customer-facing preview
                </p>
              </div>
            </div>

            {/* DEVICE SWITCH */}

            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setPreviewMode("desktop")}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition ${
                  previewMode === "desktop"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Monitor size={13} />
                Desktop
              </button>

              <button
                type="button"
                onClick={() => setPreviewMode("mobile")}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition ${
                  previewMode === "mobile"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Smartphone size={13} />
                Mobile
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------
          SECTION INFORMATION
      ------------------------------------------------------- */}

      {section && (
        <div className="max-w-[1400px] mx-auto px-6 pt-5">
          <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  {section.sectionName}
                </h2>

                <div className="flex items-center gap-2 mt-2">
                  <span className="px-2 py-1 rounded-md bg-slate-100 text-[10px] text-slate-500">
                    {section.layoutType || section.sectionName}
                  </span>

                  <span className="px-2 py-1 rounded-md bg-emerald-50 text-[10px] text-emerald-600">
                    Live Data
                  </span>

                  <span className="px-2 py-1 rounded-md bg-slate-100 text-[10px] text-slate-500">
                    Order {section.displayOrder}
                  </span>
                </div>
              </div>

              <span className="text-[11px] font-medium text-emerald-600">
                {section.isVisible ? "Active" : "Hidden"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------
          PREVIEW AREA
      ------------------------------------------------------- */}

      <div className="max-w-[1400px] mx-auto px-6 py-5">
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          {/* PREVIEW LABEL */}

          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <span className="text-[10px] font-semibold tracking-wide text-slate-400 uppercase">
              Customer Preview
            </span>
          </div>

          {/* DEVICE CANVAS */}

          <div className="bg-slate-100 p-6 min-h-[500px] flex justify-center">
            <div
              className={
                previewMode === "mobile"
                  ? "w-[390px] max-w-full bg-white rounded-[28px] shadow-xl overflow-hidden border border-slate-300"
                  : "w-full bg-white overflow-hidden"
              }
            >
              {/* MOBILE DEVICE BAR */}

              {previewMode === "mobile" && (
                <div className="h-7 bg-white flex items-center justify-center border-b border-slate-100">
                  <div className="w-20 h-1.5 rounded-full bg-slate-800" />
                </div>
              )}

              <div
                className={
                  previewMode === "mobile"
                    ? "overflow-x-hidden"
                    : "w-full overflow-x-hidden"
                }
              >
                {renderPreview()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// EMPTY PREVIEW
// ------------------------------------------------------------

const EmptyPreview: React.FC<{
  message: string;
}> = ({ message }) => {
  return (
    <div className="min-h-[300px] flex items-center justify-center">
      <div className="text-center">
        <Eye size={28} className="mx-auto text-slate-300 mb-3" />

        <p className="text-sm text-slate-400">{message}</p>
      </div>
    </div>
  );
};

export default SectionPreviewScreen;
